import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { GetCookie } from "../functions/cookies";

const DEFAULT_TRACKING_ENDPOINT = "/api/custom-tracking-event/";
const FLUSH_INTERVAL_MS = 10000;
const MAX_BATCH_SIZE = 50;

export const SESSION_START = "START";
export const SESSION_ACTIVE = "ACTIVE";
export const FORM_SUBMIT = "FORM_SUBMIT";
export const UPLOAD_FILE = "UPLOAD_FILE";

const normalizeTrackingType = (value?: string): string => {
  if (!value) return SESSION_ACTIVE;
  if (value === "form_submission" || value === "form_submit") return FORM_SUBMIT;
  if (value === "upload_file") return UPLOAD_FILE;
  if (value === SESSION_START || value === SESSION_ACTIVE || value === FORM_SUBMIT || value === UPLOAD_FILE) {
    return value;
  }
  return SESSION_ACTIVE;
};

type TrackCustomEventArgs = {
  trackingType?: string;
  eventType?: string;
  path?: string;
  searchQuery?: string;
  search?: string;
  referrer?: string;
  additionalData?: any;
};

type TrackingPayload = {
  tracking_type: string;
  path: string;
  search_query: string;
  referrer: string;
};

type TrackingProviderProps = {
  wanted_path?: string | null;
  base_path?: string | null;
  tracking_endpoint?: string | null;
};

let trackingQueue: TrackingPayload[] = [];
let flushScheduled = false;

const buildPayload = ({
  trackingType,
  eventType,
  path,
  searchQuery,
  search,
  referrer,
}: TrackCustomEventArgs): TrackingPayload => ({
  tracking_type: normalizeTrackingType(trackingType || eventType),
  path: path || window.location.pathname,
  search_query: searchQuery || search || window.location.search.replace(/^\?/, ""),
  referrer: referrer || document.referrer || "",
});

const enqueuePayload = (payload: TrackingPayload) => {
  trackingQueue.push(payload);
};

const resolveTrackingEndpoint = (endpoint?: string | null): string => {
  if (!endpoint) {
    return DEFAULT_TRACKING_ENDPOINT;
  }
  const trimmed = endpoint.trim();
  return trimmed || DEFAULT_TRACKING_ENDPOINT;
};

const applyTrackingBasePath = (path: string, basePath?: string | null): string => {
  const normalizedPath = path && path.trim() ? path.trim() : "/";
  if (!basePath) {
    return normalizedPath;
  }
  const base = basePath.replace(/^\/+|\/+$/g, "");
  if (!base) {
    return normalizedPath;
  }
  const basePrefix = `/${base}`;
  const pathWithSlash = normalizedPath.startsWith("/") ? normalizedPath : `/${normalizedPath}`;

  if (pathWithSlash === basePrefix || pathWithSlash.startsWith(`${basePrefix}/`)) {
    return pathWithSlash;
  }
  if (pathWithSlash === "/") {
    return `${basePrefix}/`;
  }
  return `${basePrefix}${pathWithSlash}`;
};

const sendBatch = (batch: TrackingPayload[], endpoint: string, useBeacon = false) => {
  if (batch.length === 0) return;
  const body = JSON.stringify({ events: batch });
  if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
    try {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(endpoint, blob);
      return;
    } catch {
      // fall through to fetch
    }
  }
  try {
    fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": GetCookie("csrfmiddlewaretoken") || "",
      },
      body,
      credentials: "same-origin",
      keepalive: useBeacon,
    }).catch(() => null);
  } catch {
    // Intentionally noop: tracking should never break UX
  }
};

const flushQueue = (endpoint: string, useBeacon = false) => {
  if (trackingQueue.length === 0) return;
  const batch = trackingQueue.splice(0, MAX_BATCH_SIZE);
  sendBatch(batch, endpoint, useBeacon);
  if (trackingQueue.length > 0 && !flushScheduled && !useBeacon) {
    flushScheduled = true;
    setTimeout(() => {
      flushScheduled = false;
      flushQueue(endpoint, false);
    }, 0);
  }
};

export const trackCustomEvent = (args: TrackCustomEventArgs) => {
  enqueuePayload(buildPayload(args));
};

export const TrackingProvider = ({
  wanted_path = null,
  base_path = null,
  tracking_endpoint = DEFAULT_TRACKING_ENDPOINT,
}: TrackingProviderProps) => {
  const location = useLocation();
  const isFirstLoad = useRef(true);
  const inFlight = useRef(false);

  useEffect(() => {
    const trackingType = isFirstLoad.current ? SESSION_START : SESSION_ACTIVE;
    trackCustomEvent({
      trackingType,
      path: applyTrackingBasePath(wanted_path || location.pathname, base_path),
      searchQuery: location.search.replace(/^\?/, ""),
    });
    isFirstLoad.current = false;
  }, [location.pathname, wanted_path, base_path, location.search]);

  useEffect(() => {
    const endpoint = resolveTrackingEndpoint(tracking_endpoint);

    const flush = (useBeacon = false) => {
      if (inFlight.current) return;
      inFlight.current = true;
      flushQueue(endpoint, useBeacon);
      inFlight.current = false;
    };

    const intervalId = window.setInterval(() => flush(false), FLUSH_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flush(true);
      }
    };
    const handleBeforeUnload = () => flush(true);

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      flush(true);
    };
  }, [tracking_endpoint]);

  return null;
};
